import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Store, Search, CheckCircle2, XCircle, AlertCircle, Clock,
    ExternalLink, ChevronDown, ChevronUp, RefreshCw, Truck,
    Star, Phone, MapPin, User, Tag, Filter, Shield, ShieldOff,
    Eye, EyeOff, Copy, Check
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const STATUS_META = {
    active:   { label: 'Activo',    className: 'bg-green-100 text-green-700 border-0',  icon: CheckCircle2 },
    pending:  { label: 'Pendiente', className: 'bg-amber-100 text-amber-700 border-0',  icon: Clock },
    rejected: { label: 'Rechazado', className: 'bg-red-100 text-red-700 border-0',     icon: XCircle },
    suspended:{ label: 'Suspendido',className: 'bg-gray-100 text-gray-600 border-0',   icon: AlertCircle },
};

const SUPER_CAT_META = {
    oklovendo:   { name: 'OkLoVendo',    icon: '🛍️', color: '#dc2626' },
    okfood:      { name: 'OkFood',       icon: '🍔', color: '#ea580c' },
    okrestaurante:{ name: 'OkRestaurante',icon: '🍽️', color: '#1d4ed8' },
    oktienda:    { name: 'OkTienda',     icon: '🏪', color: '#16a34a' },
    okshop:      { name: 'OkShop',       icon: '🏬', color: '#7c3aed' },
    okfarmacia:  { name: 'OkFarma',      icon: '💊', color: '#0891b2' },
    oktortilla:  { name: 'OkTortilla',   icon: '🌽', color: '#d97706' },
    okdrink:     { name: 'OkDrink',      icon: '🍺', color: '#b45309' },
};

function StatusBadge({ status }) {
    const m = STATUS_META[status] || { label: status, className: 'bg-gray-100 text-gray-600 border-0', icon: AlertCircle };
    const Icon = m.icon;
    return (
        <Badge className={`${m.className} inline-flex items-center gap-1`}>
            <Icon className="h-3 w-3" />{m.label}
        </Badge>
    );
}

function SuperCatBadge({ slug }) {
    const m = SUPER_CAT_META[slug] || { name: slug, icon: '🏪', color: '#6b7280' };
    return (
        <span
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full text-white font-medium"
            style={{ backgroundColor: m.color }}
        >
            {m.icon} {m.name}
        </span>
    );
}

function DemoCredentialRow({ label, value }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="flex items-center justify-between gap-2 text-xs bg-gray-50 rounded px-2 py-1">
            <span className="text-muted-foreground w-16 flex-shrink-0">{label}</span>
            <span className="font-mono flex-1 truncate">{value}</span>
            <button onClick={copy} className="flex-shrink-0 text-muted-foreground hover:text-foreground">
                {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
            </button>
        </div>
    );
}

function NegocioCard({ biz, onUpdate }) {
    const [expanded, setExpanded] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [showCreds, setShowCreds] = useState(false);
    const { toast } = useToast();

    const update = async (fields, msg) => {
        setUpdating(true);
        try {
            await api.put(`/admin/oknegocios/${biz.id}`, fields);
            toast({ title: msg });
            onUpdate();
        } catch (e) {
            toast({ title: 'Error al actualizar', variant: 'destructive' });
        } finally {
            setUpdating(false);
        }
    };

    const catMeta = SUPER_CAT_META[biz.super_category] || { name: biz.super_category, icon: '🏪' };

    return (
        <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${biz.is_demo ? 'border-blue-200' : ''}`}>
            {/* Main row */}
            <div className="p-4 flex items-start gap-3">
                {/* Logo placeholder */}
                <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 text-xl">
                    {biz.logo_url
                        ? <img src={biz.logo_url} alt={biz.name} className="w-full h-full object-cover rounded-xl" />
                        : catMeta.icon}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-sm">{biz.name}</h3>
                                {biz.verified && <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                                {biz.is_demo && (
                                    <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[10px]">DEMO</Badge>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{biz.tagline}</p>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <SuperCatBadge slug={biz.super_category} />
                                <StatusBadge status={biz.status} />
                                {biz.rating_avg > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-amber-600">
                                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                        {biz.rating_avg.toFixed(1)} ({biz.rating_count})
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {biz.status === 'pending' && (
                                <>
                                    <Button
                                        size="sm"
                                        className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                        disabled={updating}
                                        onClick={() => update({ status: 'active' }, `✓ ${biz.name} aprobado`)}
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aprobar
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                                        disabled={updating}
                                        onClick={() => update({ status: 'rejected' }, `${biz.name} rechazado`)}
                                    >
                                        <XCircle className="h-3.5 w-3.5 mr-1" />Rechazar
                                    </Button>
                                </>
                            )}
                            {biz.status === 'active' && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                                    disabled={updating}
                                    onClick={() => update({ status: 'suspended' }, `${biz.name} suspendido`)}
                                >
                                    <ShieldOff className="h-3.5 w-3.5 mr-1" />Suspender
                                </Button>
                            )}
                            {(biz.status === 'suspended' || biz.status === 'rejected') && (
                                <Button
                                    size="sm"
                                    className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                    disabled={updating}
                                    onClick={() => update({ status: 'active' }, `${biz.name} reactivado`)}
                                >
                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Reactivar
                                </Button>
                            )}
                            {biz.status === 'active' && !biz.verified && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                                    disabled={updating}
                                    onClick={() => update({ verified: true }, `${biz.name} verificado ✓`)}
                                >
                                    <Shield className="h-3.5 w-3.5 mr-1" />Verificar
                                </Button>
                            )}
                            <a
                                href={`/aliado/${biz.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 h-7 text-xs px-2 border rounded-md text-muted-foreground hover:bg-gray-50 transition-colors"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />Ver perfil
                            </a>
                            <button
                                className="h-7 w-7 flex items-center justify-center border rounded-md text-muted-foreground hover:bg-gray-50"
                                onClick={() => setExpanded(e => !e)}
                            >
                                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded details */}
            {expanded && (
                <div className="border-t bg-gray-50 px-4 py-3 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Propietario</p>
                            <div className="flex items-center gap-1.5">
                                <User className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{biz.owner_nickname || '(sin usuario vinculado)'}</span>
                            </div>
                        </div>
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Ciudad</p>
                            <div className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{biz.city || '—'}</span>
                            </div>
                        </div>
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Teléfono</p>
                            <div className="flex items-center gap-1.5">
                                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{biz.phone || '—'}</span>
                            </div>
                        </div>
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Tipo de negocio</p>
                            <div className="flex items-center gap-1.5">
                                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{biz.business_type || '—'}</span>
                            </div>
                        </div>
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Delivery</p>
                            <div className="flex items-center gap-1.5">
                                <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{biz.delivery_available ? `Sí – $${biz.delivery_cost}` : 'No'}</span>
                            </div>
                        </div>
                        <div>
                            <p className="text-muted-foreground mb-0.5 font-medium">Slug / URL</p>
                            <span className="font-mono text-blue-600">/aliado/{biz.slug}</span>
                        </div>
                    </div>

                    {biz.description && (
                        <p className="text-xs text-gray-600 border-t pt-2">{biz.description}</p>
                    )}

                    {/* Demo credentials */}
                    {biz.is_demo && biz.demo_email && (
                        <div className="border-t pt-2">
                            <button
                                className="flex items-center gap-1.5 text-xs text-blue-600 font-medium mb-2"
                                onClick={() => setShowCreds(c => !c)}
                            >
                                {showCreds ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                {showCreds ? 'Ocultar' : 'Ver'} credenciales demo
                            </button>
                            {showCreds && (
                                <div className="space-y-1.5 max-w-sm">
                                    <DemoCredentialRow label="Email" value={biz.demo_email} />
                                    <DemoCredentialRow label="Contraseña" value={biz.demo_password || 'Demo2024!'} />
                                    <p className="text-[10px] text-muted-foreground">
                                        Usa estas credenciales para acceder al dashboard del negocio demo en{' '}
                                        <a href="/aliado/dashboard" target="_blank" className="text-blue-600 underline">/aliado/dashboard</a>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ID */}
                    <p className="text-[10px] text-muted-foreground font-mono">ID: {biz.id}</p>
                </div>
            )}
        </div>
    );
}

export default function AdminOkNegocios() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [page, setPage] = useState(0);
    const LIMIT = 20;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: LIMIT, skip: page * LIMIT };
            if (q) params.q = q;
            if (statusFilter) params.status = statusFilter;
            if (catFilter) params.super_category = catFilter;
            const { data } = await api.get('/admin/oknegocios', { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter, catFilter, page]);

    useEffect(() => { load(); }, [load]);

    // Status counts summary
    const pendingCount = items.filter(b => b.status === 'pending').length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Store className="h-6 w-6 text-red-600" />
                        OkNegocios
                    </h1>
                    <p className="text-muted-foreground text-sm mt-0.5">
                        Gestión de negocios aliados · {total} en total
                        {pendingCount > 0 && (
                            <span className="ml-2 text-amber-600 font-medium">· {pendingCount} pendientes de aprobación</span>
                        )}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                </Button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Total', val: total, color: 'text-foreground' },
                    { label: 'Activos', val: items.filter(b => b.status === 'active').length, color: 'text-green-600' },
                    { label: 'Pendientes', val: pendingCount, color: 'text-amber-600' },
                    { label: 'Suspendidos', val: items.filter(b => b.status === 'suspended').length, color: 'text-gray-500' },
                ].map(s => (
                    <div key={s.label} className="bg-white rounded-xl border p-3 shadow-sm text-center">
                        <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border p-4 shadow-sm flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={e => { setQ(e.target.value); setPage(0); }}
                        placeholder="Buscar por nombre, slug o propietario…"
                        className="pl-9 h-9"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                        className="h-9 text-sm border rounded-md px-2 bg-white"
                    >
                        <option value="">Todos los estados</option>
                        <option value="active">Activos</option>
                        <option value="pending">Pendientes</option>
                        <option value="rejected">Rechazados</option>
                        <option value="suspended">Suspendidos</option>
                    </select>
                    <select
                        value={catFilter}
                        onChange={e => { setCatFilter(e.target.value); setPage(0); }}
                        className="h-9 text-sm border rounded-md px-2 bg-white"
                    >
                        <option value="">Todas las categorías</option>
                        {Object.entries(SUPER_CAT_META).map(([slug, m]) => (
                            <option key={slug} value={slug}>{m.icon} {m.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="space-y-3">
                    {[1,2,3].map(i => (
                        <div key={i} className="bg-white rounded-xl border h-20 animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center shadow-sm">
                    <Store className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No se encontraron negocios con los filtros actuales.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map(biz => (
                        <NegocioCard key={biz.id} biz={biz} onUpdate={load} />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {total > LIMIT && (
                <div className="flex items-center justify-between text-sm">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                    >Anterior</Button>
                    <span className="text-muted-foreground">
                        Página {page + 1} de {Math.ceil(total / LIMIT)}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={(page + 1) * LIMIT >= total}
                        onClick={() => setPage(p => p + 1)}
                    >Siguiente</Button>
                </div>
            )}
        </div>
    );
}
